import React from 'react'
import { SelectChangeEvent, SelectProps } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { LabeledOption } from 'src/Types/globalTypes'
import { BaseMultiSelect } from 'src/Libs/inputs/react-hook-form/Select/index'

interface Props extends Omit<SelectProps<string[]>, 'name' | 'multiple' | 'value' | 'onChange'> {
  name: string
  options: Array<LabeledOption<string> & { disabled?: boolean }>
  onChange?: (e: SelectChangeEvent<string[]>) => void
  clearAllLabel?: string
  selectAllLabel?: string
}

const mapSelectedValues = (selectedValues: string[], options: Props['options']): string[] =>
  selectedValues.reduce<string[]>((mappedValues, selectedValue) => {
    const mappedValue = options.find(option => String(option.value) === selectedValue)?.value

    if (mappedValue !== undefined) {
      mappedValues.push(mappedValue)
    }

    return mappedValues
  }, [])

/**
 * MultiSelect input integrated with react-hook-form
 */
const MultiSelect: React.FC<Props> = ({ name, options, onChange, clearAllLabel, selectAllLabel, ...props }) => {
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const fieldValues = Array.isArray(field.value) ? field.value : []
        const selectedValueStrings = fieldValues.map(String)

        return (
          <BaseMultiSelect
            {...props}
            name={field.name}
            options={options}
            value={selectedValueStrings}
            onBlur={field.onBlur}
            inputRef={field.ref}
            error={!!error}
            helperText={error ? error.message : undefined}
            clearAllLabel={clearAllLabel}
            selectAllLabel={selectAllLabel}
            onChange={selectedValues => {
              field.onChange(mapSelectedValues(selectedValues, options))
            }}
            onNativeChange={onChange}
          />
        )
      }}
    />
  )
}

export default MultiSelect
